import React, { useState } from 'react';
import type { DocumentType } from '../graphql/generated';
import { gql } from '../graphql/generated';
import { useAllTopicMap } from '../topic/useAllTopics';
import populateObjects from '../utils/populateObjects';
import sortTopicsByName from '../utils/sortTopicsByName';
import * as styles from './PostDetailPreviewHeader.module.scss';
import ControlBarContributorsButton from './controlBar/ControlBarContributorsButton';
import { ControlBarInsightsButtonContent } from './controlBar/ControlBarInsightsButtonContent';
import PostSeriesInTopicList from './series/PostSeriesInTopicList';
import PostTopicWithBreadcrumbs from './topic/PostTopicWithBreadcrumbs';

const POST_FRAGMENT = gql(`
  fragment PostDetailPreviewHeader_post on Post {
    id
    topics {
      id
    }
    series {
      id
      ...PostSeriesInTopicList_series
    }
    ...ControlBarContributorsButton_post
    ...ControlBarInsightsButtonContent_post
  }
`);

interface PostDetailPreviewHeaderProps {
  post: DocumentType<typeof POST_FRAGMENT>;
}

const PostDetailPreviewHeader = ({ post }: PostDetailPreviewHeaderProps) => {
  const [topicContainerEl, setTopicContainerEl] =
    useState<HTMLUListElement | null>(null);

  const allTopicMap = useAllTopicMap();
  const postTopics = populateObjects(
    allTopicMap,
    post.topics.map(({ id }) => id),
  );

  const itemsCount = postTopics.length + (post.series ? 1 : 0);
  const sortedTopics = sortTopicsByName(postTopics);
  const hasTopics = post.series || sortedTopics.length > 0;

  return (
    <div className={styles.headerBar}>
      <div className={styles.controlBarContainer}>
        <ControlBarContributorsButton post={post} interactionsDisabled>
          <ControlBarInsightsButtonContent post={post} />
        </ControlBarContributorsButton>
      </div>
      <div className={styles.topicBarContainer}>
        {hasTopics ? (
          <ul
            ref={setTopicContainerEl}
            className={styles.topicList}
            aria-label="Post topics"
          >
            {post.series && (
              <PostSeriesInTopicList
                postId={post.id}
                series={post.series}
                size="small"
                disableHoverView
              />
            )}
            {sortedTopics.map((topic) => (
              <PostTopicWithBreadcrumbs
                key={topic.id}
                topic={topic}
                containerEl={topicContainerEl}
                itemsPerRow={Math.min(itemsCount, 3) as 1 | 2 | 3}
                disableHoverView
                size="small"
              />
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
};

export default PostDetailPreviewHeader;
